import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { X } from 'lucide-react'
import { produce } from 'immer'
import { ROUTE_TRANSFERS } from '@klear/constants'
import { wireValidationErrorMapFromTrpcError } from '@klear/schema/wire/trpc-form-errors'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@klear/ui/components/card'
import { Combobox } from '@klear/ui/components/combobox'
import { MoneyInput } from '@klear/ui/components/money-input'
import { Label } from '@klear/ui/components/label'
import { Textarea } from '@klear/ui/components/textarea'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import { useWizardDirtyLeaveGuard } from '@klear/ui/wizard/dirty-leave-guard'
import { Wizard } from '@klear/ui/wizard/wizard'
import isError from 'lodash/isError'
import some from 'lodash/some'

import {
  EMPTY_TRANSFER_FORM,
  isTransferFormDirty,
  optionalTransferText,
  transferFormToCreateWire,
  type TransferForm
} from '@/app/transfers/transfer-create-form'
import {
  clearTransferFormErrors,
  setTransferFormField,
  type TransferFormErrorMap,
  type TransferFormFieldPath,
  type TransferFormFieldValue
} from '@/app/transfers/transfer-form-state'
import { useAllowedTransferOrgs, useTransferMutations } from '@/app/transfers/use-transfers'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

const STEP = { PARTIES: 'parties', AMOUNT: 'amount', REVIEW: 'review' } as const
type StepId = (typeof STEP)[keyof typeof STEP]

const PARTY_FIELD_PATHS: readonly TransferFormFieldPath[] = ['sourceOrgId', 'destOrgId']
const AMOUNT_FIELD_PATHS: readonly TransferFormFieldPath[] = ['money']

function hasErrors(errors: TransferFormErrorMap): boolean {
  return some(errors, Boolean)
}

function getPartyErrors(values: TransferForm): TransferFormErrorMap {
  const errors: TransferFormErrorMap = {}
  if (values.sourceOrgId == null || values.destOrgId == null) {
    if (values.sourceOrgId == null) errors.sourceOrgId = 'Select source organization'
    if (values.destOrgId == null) errors.destOrgId = 'Select destination organization'
    return errors
  }
  if (values.sourceOrgId === values.destOrgId) {
    errors.sourceOrgId = 'Source and destination must differ'
    errors.destOrgId = 'Source and destination must differ'
    return errors
  }
  return errors
}

function getAmountErrors(values: TransferForm): TransferFormErrorMap {
  if (!values.money.isPositive()) {
    return { money: 'Amount must be greater than zero' }
  }
  return {}
}

function getStepErrors(step: StepId, values: TransferForm): TransferFormErrorMap {
  if (step === STEP.PARTIES) return getPartyErrors(values)
  if (step === STEP.AMOUNT) return getAmountErrors(values)
  return {}
}

function getStepFieldPaths(step: StepId): readonly TransferFormFieldPath[] {
  if (step === STEP.PARTIES) return PARTY_FIELD_PATHS
  if (step === STEP.AMOUNT) return AMOUNT_FIELD_PATHS
  return []
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-sm text-red-500">{message}</p> : null
}

function ReviewBody({ orgNameById, values }: { orgNameById: Map<number, string>; values: TransferForm }) {
  const notes = optionalTransferText(values.notes)
  const internalNotes = optionalTransferText(values.internalNotes)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>Publish creates the Stripe outbound payment.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div>
          <Label className="text-muted-foreground">From</Label>
          <div className="font-medium">
            {values.sourceOrgId != null ? (orgNameById.get(values.sourceOrgId) ?? '—') : '—'}
          </div>
        </div>
        <div>
          <Label className="text-muted-foreground">To</Label>
          <div className="font-medium">
            {values.destOrgId != null ? (orgNameById.get(values.destOrgId) ?? '—') : '—'}
          </div>
        </div>
        <div>
          <Label className="text-muted-foreground">Amount</Label>
          <div className="font-medium">{values.money.toString()}</div>
        </div>
        <div>
          <Label className="text-muted-foreground">Network</Label>
          <div className="font-medium">Klear to Klear (instant)</div>
        </div>
        {notes ? (
          <div>
            <Label className="text-muted-foreground">Notes</Label>
            <div className="mt-1 whitespace-pre-wrap">{notes}</div>
          </div>
        ) : null}
        {internalNotes ? (
          <div>
            <Label className="text-muted-foreground">Internal Notes</Label>
            <div className="mt-1 whitespace-pre-wrap">{internalNotes}</div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

type TransferOrgOption = {
  value: number
  label: string
  keywords: string[]
  disabled?: boolean
}

type TransferStepCtx = {
  values: TransferForm
  errors: TransferFormErrorMap
  sourceOptions: TransferOrgOption[]
  destOptions: TransferOrgOption[]
  isLoadingOrgs: boolean
  orgNameById: Map<number, string>
  setFieldValue: (path: TransferFormFieldPath, value: TransferFormFieldValue) => void
  validateBeforeNext: (stepId: StepId) => boolean
}

const partiesStep: WizardStepModule<TransferStepCtx> = {
  id: STEP.PARTIES,
  label: 'Parties',
  beforeNext: (ctx) => ctx.validateBeforeNext(STEP.PARTIES),
  component: function TransferPartiesStep({ ctx }) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Pick parties</CardTitle>
          <CardDescription>Only funder organizations can be source or destination.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>From</Label>
            <Combobox<number>
              options={ctx.sourceOptions}
              value={ctx.values.sourceOrgId ?? ''}
              onChange={(v) => ctx.setFieldValue('sourceOrgId', v === '' ? null : v)}
              valueType="number"
              triggerLabel={ctx.isLoadingOrgs ? 'Loading…' : 'Select source organization'}
              disabled={ctx.isLoadingOrgs}
              className="w-full"
              error={ctx.errors.sourceOrgId ? { message: ctx.errors.sourceOrgId } : undefined}
            />
            <FieldError message={ctx.errors.sourceOrgId} />
          </div>
          <div className="space-y-1.5">
            <Label>To</Label>
            <Combobox<number>
              options={ctx.destOptions}
              value={ctx.values.destOrgId ?? ''}
              onChange={(v) => ctx.setFieldValue('destOrgId', v === '' ? null : v)}
              valueType="number"
              triggerLabel={ctx.isLoadingOrgs ? 'Loading…' : 'Select destination organization'}
              disabled={ctx.isLoadingOrgs}
              className="w-full"
              error={ctx.errors.destOrgId ? { message: ctx.errors.destOrgId } : undefined}
            />
            <FieldError message={ctx.errors.destOrgId} />
          </div>
        </CardContent>
      </Card>
    )
  }
}

const amountStep: WizardStepModule<TransferStepCtx> = {
  id: STEP.AMOUNT,
  label: 'Amount',
  beforeNext: (ctx) => ctx.validateBeforeNext(STEP.AMOUNT),
  component: function TransferAmountStep({ ctx }) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Amount & notes</CardTitle>
          <CardDescription>Klear-to-Klear (instant) network. Stripe Treasury outbound payment.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <MoneyInput
            label="Amount"
            placeholder="0.00"
            value={ctx.values.money}
            onChange={(money) => ctx.setFieldValue('money', money)}
            errorMessage={ctx.errors.money ?? ctx.errors['money.amount'] ?? ctx.errors['money.currency']}
          />
          <div className="space-y-1.5">
            <Label htmlFor="transfer-notes">Notes</Label>
            <Textarea
              id="transfer-notes"
              value={ctx.values.notes}
              onChange={(event) => ctx.setFieldValue('notes', event.target.value)}
              rows={3}
            />
            <FieldError message={ctx.errors.notes} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="transfer-internal-notes">Internal Notes</Label>
            <Textarea
              id="transfer-internal-notes"
              value={ctx.values.internalNotes}
              onChange={(event) => ctx.setFieldValue('internalNotes', event.target.value)}
              rows={3}
            />
            <FieldError message={ctx.errors.internalNotes} />
          </div>
        </CardContent>
      </Card>
    )
  }
}

const reviewStep: WizardStepModule<TransferStepCtx> = {
  id: STEP.REVIEW,
  label: 'Review',
  component: function TransferReviewStep({ ctx }) {
    return <ReviewBody orgNameById={ctx.orgNameById} values={ctx.values} />
  }
}

const TRANSFER_STEP_MODULES = [partiesStep, amountStep, reviewStep]

export default function CreateTransfer() {
  useDocumentTitle('New Transfer')
  const navigate = useNavigate()
  const [values, setValues] = useState<TransferForm>(EMPTY_TRANSFER_FORM)
  const [errors, setErrors] = useState<TransferFormErrorMap>({})
  const [nav, setNav] = useState<WizardStepGroupNavState | null>(null)
  const { LeaveConfirmDialog, guardExit } = useWizardDirtyLeaveGuard({ when: isTransferFormDirty(values) })
  const guardedExit = useMemo(() => guardExit(() => navigate(`/${ROUTE_TRANSFERS}`)), [guardExit, navigate])

  const { allowedOrgs, isLoading: isLoadingOrgs } = useAllowedTransferOrgs()
  const { createTransfer, isCreating } = useTransferMutations()

  const orgNameById = useMemo(() => new Map(allowedOrgs.map((o) => [o.id, o.name])), [allowedOrgs])
  const orgOptions = useMemo(
    () => allowedOrgs.map((org) => ({ value: org.id, label: org.name, keywords: [org.name] })),
    [allowedOrgs]
  )

  const sourceOptions = useMemo(
    () => orgOptions.map((o) => (o.value === values.destOrgId ? { ...o, disabled: true } : o)),
    [orgOptions, values.destOrgId]
  )
  const destOptions = useMemo(
    () => orgOptions.map((o) => (o.value === values.sourceOrgId ? { ...o, disabled: true } : o)),
    [orgOptions, values.sourceOrgId]
  )

  const setFieldValue = useCallback((path: TransferFormFieldPath, value: TransferFormFieldValue) => {
    setValues((current) =>
      produce(current, (draft) => {
        setTransferFormField(draft, path, value)
      })
    )
    setErrors((current) => clearTransferFormErrors(current, path))
  }, [])

  const validateBeforeNext = useCallback(
    (step: StepId) => {
      const stepErrors = getStepErrors(step, values)
      if (hasErrors(stepErrors)) {
        setErrors((current) => ({ ...current, ...stepErrors }))
        return false
      }
      setErrors((current) => clearTransferFormErrors(current, getStepFieldPaths(step)))
      return true
    },
    [values]
  )

  const stepCtx = useMemo<TransferStepCtx>(
    () => ({
      values,
      errors,
      sourceOptions,
      destOptions,
      isLoadingOrgs,
      orgNameById,
      setFieldValue,
      validateBeforeNext
    }),
    [values, errors, sourceOptions, destOptions, isLoadingOrgs, orgNameById, setFieldValue, validateBeforeNext]
  )
  const stepDefs = useMemo(() => bindWizardSteps(TRANSFER_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    stepSelection: 'free',
    onNavStateChange: setNav
  })

  const handlePublish = useCallback(async () => {
    const partyErrors = getPartyErrors(values)
    const amountErrors = getAmountErrors(values)
    const validationErrors = { ...partyErrors, ...amountErrors }
    if (hasErrors(validationErrors)) {
      setErrors(validationErrors)
      flow.setActiveStepId(hasErrors(partyErrors) ? STEP.PARTIES : STEP.AMOUNT)
      return
    }
    const input = transferFormToCreateWire(values)
    if (input == null) {
      setErrors({ root: 'Transfer form is incomplete' })
      return
    }

    try {
      setErrors({})
      const result = await createTransfer(input)
      navigate(`/${ROUTE_TRANSFERS}/${result.paymentId}`)
    } catch (err) {
      const validationErrorMap = wireValidationErrorMapFromTrpcError(err)
      if (validationErrorMap) {
        setErrors(validationErrorMap)
        return
      }
      const message = isError(err) ? err.message : 'Failed to create transfer'
      setErrors({ root: message })
    }
  }, [values, flow.setActiveStepId, createTransfer, navigate])

  return (
    <div className="h-full min-h-0">
      {LeaveConfirmDialog}
      <Wizard
        name="Add Transfer"
        steps={nav?.steps ?? []}
        activeStepId={nav?.activeStepId ?? ''}
        onStepSelect={nav?.onStepSelect}
        onBack={guardedExit}
        headerActions={
          <Button type="button" variant="outline" onClick={guardedExit} disabled={isCreating}>
            <X className="h-4 w-4" />
            Cancel
          </Button>
        }
        error={errors.root ? <p className="text-sm text-red-500">{errors.root}</p> : null}
      >
        <WizardFlowContent
          flow={flow}
          onSubmit={() => void handlePublish()}
          disabled={isCreating}
          submitLabel={isCreating ? 'Publishing…' : 'Publish'}
          navClassName="mt-8 flex justify-between gap-2"
        />
      </Wizard>
    </div>
  )
}
